import { useEffect, useMemo, useRef, useState } from 'react';
import { Frame } from './Frame';
import { FilmTable } from './FilmTable';
import { useKeptFinds } from './FilmCard';
import { CREDIT, type Slot } from '../data/film';
import { collectPieces, currentPieces, toPick, landingOf, type FilmPiece, type Part } from '../lib/pieces';
import { listMyPosts, type FeedPost } from '../lib/feed';
import { listCharacters, type Character } from '../lib/crew';
import { loadProfile, saveProfile } from '../lib/state';
import { track } from '../analytics';
import type { Challenge } from '../data/types';
import { withoutHeld } from '../lib/reveal';

/**
 * Its own moment, after the card (owner, 2026-09-21): what was just made drops
 * into a slot of the first film. The card said what you made; this says what
 * it is for. Day 1 does not come here — its card waits for the crew's picture,
 * which lands in the film on the reward (screens/Reward.tsx).
 *
 * What lands is one part of what you wrote, as its own card (owner,
 * 2026-09-22: one idea, never the card's summary line), in the slot the
 * exercise routes it to (lib/pieces.ts partsIn): the first part whose slot has
 * nothing in it, else the first part. It used to fall back to the card's
 * headline when there were no lines, which made our copy a piece ("The animal
 * I am, and the one I am becoming" seated as Starring); now nothing is seated
 * when nothing you wrote feeds the film. The row shown is the same compact row
 * Home shows (components/FilmTable.tsx).
 */
export function FilmSlotted({ challenge, day, parts, postId, onNext }: {
  challenge: Challenge; day: number; parts: Part[]; postId?: string; onNext: () => void;
}) {
  const kept = useKeptFinds();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [chars, setChars] = useState<Character[]>([]);
  useEffect(() => { listMyPosts(40).then(setPosts).catch(() => {}); listCharacters().then(setChars).catch(() => {}); }, []);
  const [profile] = useState(loadProfile);
  const collection = useMemo(() => collectPieces(kept || [], chars, withoutHeld(posts), profile.pencilled), [kept, chars, posts, profile.pencilled]);
  const current = useMemo(() => currentPieces(collection, profile.film?.picks), [collection, profile.film?.picks]);
  const [slot, setSlot] = useState<Slot | null>(null);
  const seated = useRef(false);

  // which part lands is fixed once, with the slot: after the seat is saved, the slot it filled is no longer empty
  const [landing, setLanding] = useState<{ text: string; index: number } | null>(null);
  const text = landing?.text ?? '';
  const piece: FilmPiece | null = slot && landing ? { id: postId ? `card:${postId}:${landing.index}` : 'landing', refId: postId ? `${postId}:${landing.index}` : 'landing', postId, slot, text, title: `${challenge.title} · Day ${day}`, source: 'card', at: Date.now() } : null;

  useEffect(() => {
    if (!kept || slot) return;
    const l = landingOf(parts, current);
    if (!l) return;
    setLanding({ text: l.part.text, index: l.index });
    setSlot(l.part.slot);
  }, [kept, parts, current, slot]);

  useEffect(() => {
    if (!slot || !piece || seated.current) return;
    seated.current = true;
    const p = loadProfile();
    saveProfile({
      ...p,
      pencilled: { ...(p.pencilled || {}), [slot]: { challengeId: challenge.id, title: challenge.title, headline: text, postId, at: Date.now() } },
      film: { ...(p.film || {}), picks: { ...(p.film?.picks || {}), [slot]: toPick(piece) } },
    });
    track('slotted', { slot });
    // once per card
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot]);

  const shown = piece && slot ? { ...current, [slot]: piece } : current;

  return (
    <Frame left={<span className="tag">Day {day}</span>}>
      <div className="stack rise" style={{ gap: 4 }}>
        <span className="label" style={{ color: 'var(--green-dark)' }}>Day {day} · {challenge.title}</span>
        <h1>{piece && slot ? CREDIT[slot].line : 'Your film is taking shape.'}</h1>
        {piece && slot && <p className="why">“{text}” went in as {CREDIT[slot].label.toLowerCase()}. That came from you.</p>}
      </div>
      <div style={{ marginTop: 40 }}>
        <FilmTable mode="compact" pieces={collection} current={shown} onPick={() => {}} landing={slot} />
      </div>
      <div style={{ flex: 1 }} />
      <button className="btn" onClick={onNext} disabled={!kept}>Next</button>
    </Frame>
  );
}
